import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import theme from '../../../styles/theme/theme';

// SVG icons as React components
const AirportIcon = () => (
  <Box position="relative" width="24px" height="24px">
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.55 17.1375L13.75 8.9375L17.25 5.4375C18.75 3.9375 19.25 1.9375 18.75 0.9375C17.75 0.4375 15.75 0.9375 14.25 2.4375L10.75 5.9375L2.55 4.1375C2.05 4.0375 1.65 4.2375 1.45 4.6375L1.15 5.1375C0.95 5.6375 1.05 6.1375 1.45 6.4375L6.75 9.9375L4.75 12.9375H1.75L0.75 13.9375L3.75 15.9375L5.75 18.9375L6.75 17.9375V14.9375L9.75 12.9375L13.25 18.2375C13.55 18.6375 14.05 18.7375 14.55 18.5375L15.05 18.3375C15.45 18.0375 15.65 17.6375 15.55 17.1375Z" 
        stroke="#7A0056" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </Box>
);

const CityIcon = () => (
  <Box position="relative" width="24px" height="24px">
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4.75 20.75V2.75C4.75 2.21957 4.96071 1.71086 5.33579 1.33579C5.71086 0.960714 6.21957 0.75 6.75 0.75H14.75C15.2804 0.75 15.7891 0.960714 16.1642 1.33579C16.5393 1.71086 16.75 2.21957 16.75 2.75V20.75M4.75 20.75H16.75M4.75 20.75H2.75C2.21957 20.75 1.71086 20.5393 1.33579 20.1642C0.960714 19.7891 0.75 19.2804 0.75 18.75V12.75C0.75 12.2196 0.960714 11.7109 1.33579 11.3358C1.71086 10.9607 2.21957 10.75 2.75 10.75H4.75M16.75 20.75H18.75C19.2804 20.75 19.7891 20.5393 20.1642 20.1642C20.5393 19.7891 20.75 19.2804 20.75 18.75V9.75C20.75 9.21957 20.5393 8.71086 20.1642 8.33579C19.7891 7.96071 19.2804 7.75 18.75 7.75H16.75M8.75 4.75H12.75M8.75 8.75H12.75M8.75 12.75H12.75M8.75 16.75H12.75" 
        stroke="#7A0056" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </Box>
);

// Styled components
const MenuContainer = styled(Paper)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '4px 0px',
  width: '100%',
  borderRadius: '8px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
});

const MenuItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '1px 6px',
  width: '100%',
  borderRadius: '0px',
}));

const MenuItemContent = styled(Box)(({ theme, selected }: { theme: unknown, selected?: boolean }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '8px 10px',
  gap: '12px',
  width: '100%',
  borderRadius: '6px',
  backgroundColor: selected ? 'rgba(122, 0, 86, 0.05)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(122, 0, 86, 0.05)',
    cursor: 'pointer',
  },
}));

const IconTextContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '0px',
  gap: '8px',
  width: '100%',
});

const TextContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '0px',
  gap: '4px',
  flexGrow: 1,
});

const MenuPrimaryText = styled(Typography)({
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '21px',
  color: '#181D27',
});

const MenuSecondaryText = styled(Typography)({
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '20px',
  color: '#717680',
});

export interface DropdownItem {
  id: string;
  primaryText: string;
  secondaryText?: string;
  icon: 'airport' | 'city';
}

interface DropdownMenuProps {
  items: DropdownItem[];
  onSelect: (item: DropdownItem) => void;
  selectedItem?: string;
  open: boolean;
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  maxHeight?: number;
  width?: number | string;
  isLoading?: boolean;
  emptyMessage?: string;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  onSelect,
  selectedItem,
  open,
  anchorEl,
  onClose,
  maxHeight = 264,
  width = 388,
  isLoading = false,
  emptyMessage = 'No results found',
}) => {
  const [menuTop, setMenuTop] = useState(0);
  const [menuLeft, setMenuLeft] = useState(0);
  const [menuMaxHeight, setMenuMaxHeight] = useState(maxHeight);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (open && anchorEl && menuRef.current) {
        const anchorRect = anchorEl.getBoundingClientRect();
        const GAP = 4;
        // Use the visual viewport so the mobile keyboard (which shrinks it, not the
        // layout viewport) is accounted for — otherwise the dropdown can be positioned
        // past the visible area and appear to float above the field instead of below it.
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const top = anchorRect.bottom + GAP;

        // Always open below the input — sticky to the field. Clamp height to the space
        // actually left on screen instead of letting it extend past the keyboard.
        setMenuTop(top);
        setMenuMaxHeight(Math.max(120, Math.min(maxHeight, viewportHeight - top - GAP)));

        setMenuLeft(anchorRect.left);
        // If width is '100%', match the anchor element's width
        if (width === '100%' && menuRef.current) {
          menuRef.current.style.width = `${anchorRect.width}px`;
        }
      }
    };

    updatePosition();

    // Update position on scroll/resize, including the visual viewport resizing
    // when the mobile keyboard opens/closes.
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [open, anchorEl, maxHeight, width]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        menuRef.current &&
        anchorEl &&
        !menuRef.current.contains(event.target as Node) &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, anchorEl, onClose]);

  const handleItemClick = (item: DropdownItem) => {
    onSelect(item);
    onClose();
  };

  // If not open, don't render anything
  if (!open) {
    return null;
  }

  return (
    <Box
      ref={menuRef}
      sx={{
        position: 'fixed',
        top: menuTop,
        left: menuLeft,
        zIndex: 9999,
        width: width,
        maxHeight: menuMaxHeight,
        overflowY: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <MenuContainer>
        {isLoading ? (
          <MenuItem>
            <MenuSecondaryText sx={{ padding: '8px 10px' }}>Loading...</MenuSecondaryText>
          </MenuItem>
        ) : items.length === 0 ? (
          <MenuItem>
            <MenuSecondaryText sx={{ padding: '8px 10px' }}>{emptyMessage}</MenuSecondaryText>
          </MenuItem>
        ) : (
          items.map((item) => (
            <MenuItem key={item.id}>
              <MenuItemContent
                selected={selectedItem === item.id}
                onClick={() => handleItemClick(item)}
              >
                <IconTextContainer>
                  {item.icon === 'airport' ? <AirportIcon /> : <CityIcon />}
                  <TextContainer>
                    <MenuPrimaryText>{item.primaryText}</MenuPrimaryText>
                    {item.secondaryText && <MenuSecondaryText>{item.secondaryText}</MenuSecondaryText>}
                  </TextContainer>
                </IconTextContainer>
              </MenuItemContent>
            </MenuItem>
          ))
        )}
      </MenuContainer>
    </Box>
  );
};

export default DropdownMenu;